import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';

interface BackendHealthStatus {
  isOnline: boolean;
  lastCheck: Date | null;
  error: string | null;
}

interface UseBackendHealthOptions {
  checkInterval?: number; 
  timeout?: number; 
  onStatusChange?: (isOnline: boolean) => void;
}

export const useBackendHealth = ({
  checkInterval = 15000,
  timeout = 3000, 
  onStatusChange,
}: UseBackendHealthOptions = {}) => {
  const [status, setStatus] = useState<BackendHealthStatus>({
    isOnline: true,
    lastCheck: null,
    error: null,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const wasOfflineRef = useRef(false);
  const toastIdRef = useRef<string | null>(null);
  const isCheckingRef = useRef(false); 
  const onStatusChangeRef = useRef(onStatusChange);


  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  const checkBackendHealth = useCallback(async () => {
    if (isCheckingRef.current) {
      return status.isOnline;
    }

    isCheckingRef.current = true;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/communications/health`,
        {
          method: 'GET',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        }
      );

      clearTimeout(timeoutId);

      const isOnline = response.ok;

      setStatus({
        isOnline,
        lastCheck: new Date(),
        error: isOnline ? null : `Server responded with status ${response.status}`,
      });

      if (isOnline && wasOfflineRef.current) {
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
          toastIdRef.current = null;
        }
        toast.success('Conexión restablecida con el servidor', {
          duration: 4000,
          icon: '✅',
        });
        wasOfflineRef.current = false;
      }

      if (onStatusChangeRef.current && wasOfflineRef.current !== !isOnline) {
        onStatusChangeRef.current(isOnline);
      }

      return isOnline;
    } catch (error: any) {
      clearTimeout(timeoutId);

      const errorMessage = error.name === 'AbortError'
        ? 'Timeout al conectar con el servidor'
        : 'No se puede conectar con el servidor';

      setStatus({
        isOnline: false,
        lastCheck: new Date(),
        error: errorMessage,
      });

      if (!wasOfflineRef.current) {
        toastIdRef.current = toast.error(
          'Conexión perdida con el servidor. Reintentando...',
          {
            duration: Infinity,
            icon: '⚠️',
          }
        );
        wasOfflineRef.current = true;
      }

      if (onStatusChangeRef.current) {
        onStatusChangeRef.current(false);
      }

      return false;
    } finally {
      isCheckingRef.current = false;
    }
  }, [timeout]); 

  useEffect(() => {
    checkBackendHealth();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(checkBackendHealth, checkInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
      isCheckingRef.current = false;
    };
  }, [checkBackendHealth, checkInterval]);

  const recheckNow = useCallback(() => {
    return checkBackendHealth();
  }, [checkBackendHealth]);

  return {
    isOnline: status.isOnline,
    lastCheck: status.lastCheck,
    error: status.error,
    recheckNow,
  };
};
